/**
 * Talmia II — Navigation config.
 *
 * The 9 WORKING sections are now built from `nav_manifest.json` — the single
 * source of truth shared with the backend Access Matrix (page_id == route
 * path). Icons/colours are attached from `navIcons.js` (icons can't live in
 * JSON). Roadmap groups + AOB stay as static JS below because they are
 * intentionally EXCLUDED from the access matrix.
 *
 * FLAGS
 *   disabled: true   → greyed-out "TBD" leaf (Phase 2).
 *   roadmap: true    → aspirational group under the ROADMAP divider.
 *   type: "link"     → single leaf-level nav entry.
 *   type: "group"    → collapsible group with children.
 *   type: "divider"  → visual separator row.
 *   adminOnly: true  → hidden from non-admins.
 */
import {
  Crown, ShoppingCart, Megaphone, Bot, Package, Truck, Cpu, BarChart3,
  Archive, Sparkles, LayoutDashboard, AlertTriangle, Bell, Radar,
  FileText, PhoneCall, TrendingUp, GitBranch,
  Send, Palette, Search, Percent, Radio,
  Zap, Workflow,
  Boxes, Tag, RotateCcw, Factory, Briefcase,
  Plug, Server, Activity, PieChart, LineChart, Mail,
  Package as PackageIcon, ShoppingBag, UserRound, Images,
} from "lucide-react";

import manifest from "./nav_manifest.json";
import { iconFor } from "./navIcons";

// ─── Build the 9 working sections from the manifest ────────────────────
const workingGroups = manifest.sections.map((sec) => {
  const secMeta = iconFor(sec.page_id);
  const children = (sec.children || []).map((c) => {
    const m = iconFor(c.page_id);
    return {
      to: c.page_id,
      page_id: c.page_id,
      label: c.label,
      icon: m.icon,
      color: m.color,
      ...(c.disabled ? { disabled: true } : {}),
      ...(c.admin_only ? { adminOnly: true } : {}),
      ...(c.requires_cap ? { requiresCap: c.requires_cap } : {}),
    };
  });

  if (sec.is_link) {
    // Standalone top-level link (Dev Tracker, Hub) — render as a single row.
    const child = children[0] || {};
    return {
      type: "link", key: sec.page_id, page_id: sec.page_id,
      label: sec.label, to: child.to, icon: secMeta.icon, color: secMeta.color,
    };
  }

  return {
    type: "group", key: sec.page_id, page_id: sec.page_id, label: sec.label,
    icon: secMeta.icon, color: secMeta.color,
    ...(sec.admin_only ? { adminOnly: true } : {}),
    ...(sec.manager_only ? { managerOnly: true } : {}),
    children,
  };
});

// ─── ROADMAP GROUPS (100% disabled — aspirational; NOT in the matrix) ──
const roadmapGroups = [
  {
    type: "group", key: "boss", label: "Boss Mode", icon: Crown, color: "text-amber-300", roadmap: true,
    children: [
      { to: "/boss/briefing",     label: "Daily Briefing",   icon: Sparkles,        color: "text-amber-300",  disabled: true },
      { to: "/boss/kpi",          label: "KPI Board",        icon: LayoutDashboard, color: "text-emerald-300",disabled: true },
      { to: "/boss/anomalies",    label: "Anomaly Feed",     icon: AlertTriangle,   color: "text-rose-300",   disabled: true },
      { to: "/boss/staff-alerts", label: "Staff Alerts",     icon: Bell,            color: "text-orange-300", disabled: true },
      { to: "/boss/widgets",      label: "Custom Widgets",   icon: LayoutDashboard, color: "text-sky-300",    disabled: true },
      { to: "/boss/ai",           label: "AI Analysis",      icon: Radar,           color: "text-fuchsia-300",disabled: true },
    ],
  },
  {
    type: "group", key: "marketing", label: "Marketing", icon: Megaphone, color: "text-fuchsia-300", roadmap: true,
    children: [
      { to: "/mkt/campaigns",   label: "Campaigns",       icon: Send,    color: "text-fuchsia-300", disabled: true },
      { to: "/mkt/content",     label: "Content Gen (AI)",icon: Palette, color: "text-orange-300",  disabled: true },
      { to: "/mkt/seo-ppc",     label: "SEO & PPC",       icon: Search,  color: "text-teal-300",    disabled: true },
      { to: "/mkt/promotions",  label: "Promotions",      icon: Percent, color: "text-amber-300",   disabled: true },
      { to: "/mkt/attribution", label: "Attribution",     icon: Radio,   color: "text-emerald-300", disabled: true },
    ],
  },
  {
    type: "group", key: "catalogue", label: "Catalogue", icon: Package, color: "text-sky-300", roadmap: true,
    children: [
      { to: "/catalogue/inventory", label: "Inventory (Everywhere)", icon: Boxes, color: "text-sky-300",     disabled: true },
      { to: "/catalogue/pricing",   label: "Pricing & Vendors",       icon: Tag,   color: "text-emerald-300", disabled: true },
    ],
  },
  {
    type: "group", key: "ops-roadmap", label: "Operations", icon: Truck, color: "text-teal-300", roadmap: true,
    children: [
      { to: "/ops/shipping",   label: "Shipping / Carrier",   icon: Truck,     color: "text-teal-300",    disabled: true },
      { to: "/ops/returns",    label: "Returns & Exceptions", icon: RotateCcw, color: "text-orange-300",  disabled: true },
      { to: "/ops/suppliers",  label: "Suppliers (ops)",      icon: Factory,   color: "text-amber-300",   disabled: true },
      { to: "/ops/hr",         label: "HR & Payroll",         icon: Briefcase, color: "text-indigo-300",  disabled: true },
    ],
  },
  {
    type: "group", key: "engine", label: "Engine", icon: Cpu, color: "text-indigo-300", roadmap: true,
    children: [
      { to: "/engine/apps",     label: "Connected Apps",     icon: Plug,     color: "text-indigo-300",  disabled: true },
      { to: "/engine/mcp",      label: "MCP Connectors",     icon: Server,   color: "text-fuchsia-300", disabled: true },
      { to: "/engine/health",   label: "API Health / Sync",  icon: Activity, color: "text-emerald-300", disabled: true },
      { to: "/engine/pipeline", label: "Data Pipeline",      icon: GitBranch,color: "text-teal-300",    disabled: true },
    ],
  },
  {
    type: "group", key: "analytics", label: "Analytics", icon: BarChart3, color: "text-emerald-300", roadmap: true,
    children: [
      { to: "/analytics/bi",       label: "Cross-Domain BI",   icon: PieChart,  color: "text-emerald-300", disabled: true },
      { to: "/analytics/reports",  label: "Report Builder",    icon: LineChart, color: "text-teal-300",    disabled: true },
      { to: "/analytics/digests",  label: "Scheduled Digests", icon: Mail,      color: "text-orange-300",  disabled: true },
    ],
  },
];

// ─── AOB (Legacy — retired from the launch menu; kept for reversibility) ──
// eslint-disable-next-line no-unused-vars
const aobLegacy = {
  type: "group", key: "aob", label: "AOB", icon: Archive, color: "text-slate-500",
  children: [
    { to: "/quotes/mine",       label: "My Quotes (old)",       icon: FileText,     color: "text-slate-500" },
    { to: "/quotes/all",        label: "All Quotes (old)",      icon: FileText,     color: "text-slate-500" },
    { to: "/admin/quotes",      label: "Admin — Quotes",        icon: FileText,     color: "text-slate-500", adminOnly: true },
    { to: "/admin/products",    label: "Admin — Products",      icon: PackageIcon,  color: "text-slate-500", adminOnly: true },
    { to: "/admin/orders",      label: "Admin — Orders",        icon: ShoppingBag,  color: "text-slate-500", adminOnly: true },
    { to: "/admin/customers",   label: "Admin — Customers",     icon: UserRound,    color: "text-slate-500", adminOnly: true },
    { to: "/admin/persons",     label: "Admin — Persons",       icon: UserRound,    color: "text-slate-500", adminOnly: true },
    { to: "/admin/calls",       label: "Admin — Calls",         icon: PhoneCall,    color: "text-slate-500", adminOnly: true },
    { to: "/admin/suppliers",   label: "Admin — Suppliers",     icon: Factory,      color: "text-slate-500", adminOnly: true },
  ],
};

// ─── Final ordered nav ─────────────────────────────────────────────────
const nav = [
  { type: "link", key: "my-dashboard", label: "My Dashboard", to: "/my-dashboard", icon: LayoutDashboard, color: "text-orange-300", neverGate: true },
  ...workingGroups,   // 9 sections from manifest
  { type: "link", key: "media-library", label: "Media Library", to: "/media", icon: Images, color: "text-teal-300", neverGate: true },
  // AOB (legacy) retired from the menu at launch — see menu-cleanup tracker.
  // `aobLegacy` const kept + routes untouched so it's fully reversible.
  { type: "divider", key: "div-roadmap", label: "Roadmap" },
  ...roadmapGroups,
];

/**
 * Filter the nav for the current user, honouring:
 *  - `adminOnly` (drops items when user isn't admin)
 *  - `hideRoadmap` — drops roadmap groups + disabled children
 *  - `access` — the effective Access Matrix grant. When provided and NOT
 *    unrestricted, only page_ids in `access.pages` survive. Auth admins &
 *    legacy users pass `unrestricted: true` and skip this gate entirely.
 *    Roadmap + AOB groups are never access-gated (they aren't in the matrix).
 */
export function getNavForUser(user, opts = {}) {
  const { hideRoadmap = false, access = null } = opts;
  const isAdmin = user?.role === "admin";
  const isManager = isAdmin
    || ["manager", "admin", "owner", "system_admin"].includes((user?.access_role || "").toLowerCase())
    || ["manager", "owner", "system_admin"].includes((user?.role_workspace || "").toLowerCase());
  const restricted = access && access.unrestricted === false;
  const granted = restricted ? new Set(access.pages || []) : null;

  const caps = new Set(access?.capabilities || []);
  const allowLeaf = (c, isMatrixGroup) => {
    if (c.adminOnly && !isAdmin) return false;
    if (c.requiresCap && !caps.has(c.requiresCap)) return false;
    if (hideRoadmap && c.disabled) return false;
    // Access-gate only matrix (working) leaves, never roadmap/AOB/management.
    if (restricted && isMatrixGroup && !granted.has(c.to)) return false;
    return true;
  };

  const filtered = nav
    .map((n) => {
      if (n.adminOnly && !isAdmin) return null;
      const capKids = (n.children || []).filter((c) => c.requiresCap && caps.has(c.requiresCap));
      if (n.managerOnly && !isManager && capKids.length === 0) return null;
      if (n.managerOnly && !isManager) return { ...n, children: capKids };
      if (hideRoadmap && n.roadmap) return null;
      // Management is manager-gated (above), NOT part of the access matrix.
      const isMatrixGroup = !n.roadmap && n.key !== "aob" && n.key !== "management";

      if (n.type === "link") {
        if (restricted && isMatrixGroup && !n.neverGate && !granted.has(n.to)) return null;
        return n;
      }
      if (n.type !== "group") return n;

      const kids = (n.children || []).filter((c) => allowLeaf(c, isMatrixGroup));
      if (kids.length === 0) return null;
      return { ...n, children: kids };
    })
    .filter(Boolean);

  // Prune the Roadmap divider if hidden or if nothing follows it.
  const out = [];
  filtered.forEach((n, i) => {
    if (n.type === "divider" && n.key === "div-roadmap") {
      const next = filtered[i + 1];
      if (!next || next.type === "divider") return;
      if (hideRoadmap) return;
    }
    out.push(n);
  });
  return out;
}

export default nav;
